import { forwardRef, useState, useEffect, useId } from 'react';
import styled from '@emotion/styled';

import { Message } from '../Message';

import type { SwitchProps } from './Switch.types';
import type { Theme } from '../../theme';

/**
 * Size configurations for the switch
 */
const sizeConfig = {
  sm: {
    width: 32,
    height: 16,
    thumbSize: 12,
    thumbMargin: 2,
    thumbOffset: 18, // width - thumbSize - thumbMargin
  },
  md: {
    width: 40,
    height: 20,
    thumbSize: 16,
    thumbMargin: 2,
    thumbOffset: 22, // width - thumbSize - thumbMargin
  },
  lg: {
    width: 48,
    height: 24,
    thumbSize: 20,
    thumbMargin: 2,
    thumbOffset: 26, // width - thumbSize - thumbMargin
  },
} as const;

/**
 * Wrapper container for the switch
 */
const SwitchWrapper = styled.div<{ disabled?: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: visible;
`;

/**
 * Container for switch and label
 */
const SwitchContainer = styled.div<{
  disabled?: boolean;
  labelPosition: 'left' | 'right';
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-direction: ${({ labelPosition }) =>
    labelPosition === 'left' ? 'row-reverse' : 'row'};
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};
`;

/**
 * Hidden checkbox input for accessibility
 */
const HiddenInput = styled.input`
  opacity: 0;
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
`;

/**
 * Visual track of the switch
 */
const SwitchTrack = styled.span<{
  theme?: Theme;
  $checked: boolean;
  $disabled: boolean;
  $size: 'sm' | 'md' | 'lg';
  $accentColor?: string;
}>`
  position: relative;
  display: inline-flex;
  align-items: center;
  width: ${({ $size }) => sizeConfig[$size].width}px;
  height: ${({ $size }) => sizeConfig[$size].height}px;
  border-radius: ${({ $size }) => sizeConfig[$size].height / 2}px;
  background-color: ${({ theme, $checked, $accentColor }) =>
    $checked
      ? ($accentColor || theme.colors.highlight)
      : theme.colors.grey[500]};
  transition: background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
  flex-shrink: 0;

  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.5;
    cursor: not-allowed;
  `}

  /* Hover effect */
  ${({ $disabled, theme }) =>
    !$disabled &&
    `
    &:hover {
      box-shadow: 0 0 0 2px ${theme.colors.transparent.black20};
    }
  `}
`;

/**
 * Thumb/toggle of the switch
 */
const SwitchThumb = styled.span<{
  theme?: Theme;
  $checked: boolean;
  $size: 'sm' | 'md' | 'lg';
}>`
  position: absolute;
  width: ${({ $size }) => sizeConfig[$size].thumbSize}px;
  height: ${({ $size }) => sizeConfig[$size].thumbSize}px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.colors.white};
  margin-left: ${({ $checked, $size }) =>
    $checked
      ? `${sizeConfig[$size].thumbOffset}px`
      : `${sizeConfig[$size].thumbMargin}px`};
  transition: margin-left 0.2s ease-in-out;
`;

/**
 * Label text for the switch
 */
const SwitchLabel = styled.label<{
  theme?: Theme;
  $disabled?: boolean;
}>`
  font-family: ${({ theme }) => theme.typography.fontFamily};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  line-height: ${({ theme }) => theme.typography.lineHeight.normal};
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.grey[400] : theme.colors.grey[700]};
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  user-select: none;
`;

/**
 * Icon container for left/right icons
 */
const IconContainer = styled.span<{
  theme?: Theme;
  $disabled?: boolean;
  $size: 'sm' | 'md' | 'lg';
}>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ theme, $disabled }) =>
    $disabled ? theme.colors.grey[400] : theme.colors.grey[600]};
  
  /* Size-based icon sizing */
  font-size: ${({ $size }) => {
    switch ($size) {
      case 'sm': return '14px';
      case 'lg': return '20px';
      default: return '16px';
    }
  }};

  svg {
    width: 1em;
    height: 1em;
  }
`;

/**
 * Container for messages
 */
const MessageContainer = styled.div`
  margin-top: 4px;
`;

/**
 * Switch - A toggle switch component for boolean values
 *
 * Supports controlled and uncontrolled modes, with optional label and messages.
 * Uses the theme's feature color for the checked state.
 *
 * @example
 * ```tsx
 * // Controlled
 * <Switch
 *   checked={isEnabled}
 *   onChange={(checked) => setIsEnabled(checked)}
 *   label="Enable notifications"
 * />
 *
 * // Uncontrolled
 * <Switch
 *   defaultChecked={true}
 *   label="Dark mode"
 * />
 *
 * // With error message
 * <Switch
 *   checked={false}
 *   errorMessage="This setting is required"
 * />
 * ```
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  (
    {
      checked: controlledChecked,
      defaultChecked = false,
      onChange,
      disabled = false,
      label,
      labelPosition = 'right',
      size = 'md',
      name,
      id: providedId,
      'aria-label': ariaLabel,
      errorMessage,
      assistiveMessage,
      leftIcon,
      rightIcon,
      className,
      'data-testid': testId,
      dataTestId,
      dataId,
      accentColor,
    },
    ref
  ) => {
    const generatedId = useId();
    const switchId = providedId || generatedId;

    // Determine if controlled or uncontrolled
    const isControlled = controlledChecked !== undefined;
    const [internalChecked, setInternalChecked] = useState(defaultChecked);

    const isChecked = isControlled ? controlledChecked : internalChecked;

    // Sync internal state with controlled value
    useEffect(() => {
      if (isControlled) {
        setInternalChecked(controlledChecked);
      }
    }, [isControlled, controlledChecked]);

    const handleToggle = (
      event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>
    ) => {
      if (disabled) return;

      event.preventDefault();
      event.stopPropagation();

      const newChecked = !isChecked;

      if (!isControlled) {
        setInternalChecked(newChecked);
      }

      onChange?.(newChecked, event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (disabled) return;

      if (event.key === ' ' || event.key === 'Enter') {
        handleToggle(event);
      }
    };

    return (
      <SwitchWrapper className={className} data-testid={dataTestId || testId} data-id={dataId}>
        <SwitchContainer
          onClick={handleToggle}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          labelPosition={labelPosition}
          role="switch"
          aria-checked={isChecked}
          aria-label={!label ? ariaLabel || 'Toggle switch' : undefined}
          aria-disabled={disabled}
          tabIndex={disabled ? -1 : 0}
        >
          <HiddenInput
            ref={ref}
            type="checkbox"
            id={switchId}
            name={name}
            checked={isChecked}
            disabled={disabled}
            readOnly
            tabIndex={-1}
            aria-hidden="true"
          />
          {leftIcon && (
            <IconContainer $disabled={disabled} $size={size} aria-hidden="true">
              {leftIcon}
            </IconContainer>
          )}
          <SwitchTrack
            $checked={isChecked}
            $disabled={disabled}
            $size={size}
            $accentColor={accentColor}
            data-testid={testId && `${testId}-track`}
          >
            <SwitchThumb $checked={isChecked} $size={size} />
          </SwitchTrack>
          {rightIcon && (
            <IconContainer $disabled={disabled} $size={size} aria-hidden="true">
              {rightIcon}
            </IconContainer>
          )}
          {label && (
            <SwitchLabel htmlFor={switchId} $disabled={disabled}>
              {label}
            </SwitchLabel>
          )}
        </SwitchContainer>
        {errorMessage && (
          <MessageContainer>
            <Message variant="error">{errorMessage}</Message>
          </MessageContainer>
        )}
        {assistiveMessage && !errorMessage && (
          <MessageContainer>
            <Message variant="assistive">{assistiveMessage}</Message>
          </MessageContainer>
        )}
      </SwitchWrapper>
    );
  }
);

Switch.displayName = 'Switch';

export default Switch;
